/**
 * Resume template catalogue — the single source of truth for the 70 parametric
 * template variants and their design presets, shared by:
 *
 * - apps/frontend  — the builder UI, template picker, and on-screen preview
 * - apps/backend   — the PDF export renderer (ResumeHtmlBuilder) and the
 *                    DOCX export mapper (ResumeDocxBuilder)
 *
 * Before this module existed the backend kept a hand-mirrored copy of the
 * catalogue that drifted from the frontend's. Both sides now import from
 * here, so a template edit lands on every surface at once by construction.
 *
 * Also exports the small pure (framework-free) content helpers that both
 * renderers need: stats derivation, initials, rich-text/bullets resolution.
 */
export const TEMPLATE_CATEGORIES = [
    {
        id: 'minimal',
        name: 'Modern Minimal',
        icon: 'sparkles',
        ats: 'mixed',
        note: 'The flagship default — works for most corporate / IT / consulting roles.',
    },
    {
        id: 'twocol',
        name: 'Two-Column Compact',
        icon: 'layout-grid',
        ats: 'no',
        note: 'Maximizes one-page density. Best for human review — may not parse cleanly in strict ATS.',
    },
    {
        id: 'dev',
        name: 'Technical / Developer',
        icon: 'zap',
        ats: 'mixed',
        note: 'Projects-first hierarchy, built for engineering & CS roles.',
    },
    {
        id: 'creative',
        name: 'Creative / Portfolio',
        icon: 'compass',
        ats: 'no',
        note: 'For design / marketing / media. Not recommended for ATS-heavy drives.',
    },
    {
        id: 'exec',
        name: 'Executive / Senior',
        icon: 'award',
        ats: 'mixed',
        note: 'Boardroom-formal treatments for senior & lateral roles.',
    },
    {
        id: 'academic',
        name: 'Academic / Research',
        icon: 'graduation-cap',
        ats: 'yes',
        note: 'Publications-first, multi-page CVs for postgraduate / research tracks.',
    },
];
/** Human-readable explanation per ATS rating, used by warning surfaces. */
export const ATS_RATING_COPY = {
    yes: {
        label: 'ATS-safe',
        detail: 'Single-column structure with parser-friendly text — safe for automated screening.',
    },
    conditional: {
        label: 'ATS caution',
        detail: 'Mostly parser-friendly, but decorative elements (icons, badges, colored blocks) may confuse strict ATS parsers. Prefer an ATS-Safe template for PSU / government / bank portals.',
    },
    no: {
        label: 'Not ATS-safe',
        detail: 'Multi-column or graphic-led layout that strict ATS parsers often misread. Use for human review only — switch to an ATS-Safe template for automated screening.',
    },
};
// ─── Accents ────────────────────────────────────────────────────────────────
export const ACCENTS = [
    { id: 'teal', name: 'Teal', hex: '#2E9982' },
    { id: 'navy', name: 'Navy', hex: '#1B2A4E' },
    { id: 'blue', name: 'Blue', hex: '#3A62B5' },
    { id: 'plum', name: 'Plum', hex: '#6C4BB0' },
    { id: 'amber', name: 'Amber', hex: '#C9802E' },
    { id: 'slate', name: 'Graphite', hex: '#3A4A6E' },
];
export function accentHex(accent) {
    return ACCENTS.find((a) => a.id === accent)?.hex ?? (accent.startsWith('#') ? accent : '#2E9982');
}
export const FONTS = [
    {
        id: 'inter',
        label: 'Inter',
        category: 'Sans',
        stack: "'Inter', system-ui, sans-serif",
        docx: 'Inter',
    },
    {
        id: 'figtree',
        label: 'Figtree',
        category: 'Sans',
        stack: "'Figtree', system-ui, sans-serif",
        docx: 'Figtree',
    },
    {
        id: 'source',
        label: 'Source Sans 3',
        category: 'Sans',
        stack: "'Source Sans 3', system-ui, sans-serif",
        docx: 'Source Sans 3',
    },
    {
        id: 'franklin',
        label: 'Libre Franklin',
        category: 'Sans',
        stack: "'Libre Franklin', system-ui, sans-serif",
        docx: 'Libre Franklin',
    },
    {
        id: 'plex',
        label: 'IBM Plex Sans',
        category: 'Sans',
        stack: "'IBM Plex Sans', system-ui, sans-serif",
        docx: 'IBM Plex Sans',
    },
    {
        id: 'lora',
        label: 'Lora',
        category: 'Serif',
        stack: "'Lora', Georgia, serif",
        docx: 'Lora',
    },
    {
        id: 'newsreader',
        label: 'Newsreader',
        category: 'Serif',
        stack: "'Newsreader', Georgia, serif",
        docx: 'Newsreader',
    },
    {
        id: 'merri',
        label: 'Merriweather',
        category: 'Serif',
        stack: "'Merriweather', Georgia, serif",
        docx: 'Merriweather',
    },
    {
        id: 'georgia',
        label: 'Georgia',
        category: 'Serif',
        stack: "Georgia, 'Times New Roman', serif",
        docx: 'Georgia',
    },
];
/** Sections short enough for the sidebar. The sidebar renders on page 1
 * only (it doesn't paginate), so long sections like experience must stay in
 * the main column or they'd run off the page. */
export const SIDEBAR_ELIGIBLE = new Set([
    'summary',
    'skills',
    'languages',
    'certs',
    'education',
    'interests',
    'awards',
    'achievements',
    'references',
]);
/** Does this section render in the sidebar of a two-column template? The
 * user's per-section choice wins over the template default. Shared by the
 * live preview and the PDF builder so both place sections identically. */
export function isAsideSection(section, t) {
    if (t.layout !== 'sidebar-left' && t.layout !== 'sidebar-right')
        return false;
    if (section.column === 'main')
        return false;
    if (section.column === 'aside')
        return SIDEBAR_ELIGIBLE.has(section.type);
    return t.sidebarTypes.includes(section.type);
}
/** Curated heading/body pairings from the bundled FONTS. Offered as one-tap
 * presets in the Design tab; any other combination is still allowed. */
export const FONT_PAIRS = [
    { id: 'editorial', label: 'Editorial', heading: 'newsreader', body: 'source' },
    { id: 'literary', label: 'Literary', heading: 'lora', body: 'inter' },
    { id: 'strong', label: 'Strong', heading: 'franklin', body: 'figtree' },
    { id: 'formal', label: 'Formal', heading: 'merri', body: 'plex' },
];
/** The heading font to render, or null when headings use the body font. */
export function headingFontOf(design) {
    return design.headingFont && design.headingFont !== design.font ? design.headingFont : null;
}
export function fontStack(fontId) {
    return (FONTS.find((f) => f.id === fontId) ?? FONTS[0]).stack;
}
export function docxFontName(fontId) {
    return (FONTS.find((f) => f.id === fontId) ?? FONTS[0]).docx;
}
/** Word-native fonts safe to assume are actually installed wherever a .docx
 * gets opened — everything else in FONTS is a Google Font, which the PDF
 * export can safely reference (Chromium renders it with an embedded/loaded
 * font file) but a .docx can only *request by name*: Word silently
 * substitutes an unrelated font if the reader's machine doesn't have it
 * installed, with no warning to the reader. */
const DOCX_SAFE_FONTS = new Set(['Georgia']);
/** Nearest Word-native substitute for each Google Font in FONTS, chosen for
 * visual family (geometric/humanist sans, grotesque sans, transitional
 * serif, slab-ish serif) rather than picking one default for everything. */
const DOCX_FONT_SUBSTITUTES = {
    inter: 'Calibri',
    figtree: 'Calibri',
    source: 'Calibri',
    franklin: 'Arial',
    plex: 'Calibri',
    lora: 'Cambria',
    newsreader: 'Cambria',
    merri: 'Georgia',
};
/** True when this font would silently substitute in Word — i.e. the export
 * menu should warn the user their .docx won't visually match the preview. */
export function isDocxUnsafeFont(fontId) {
    const name = docxFontName(fontId);
    return !DOCX_SAFE_FONTS.has(name);
}
/** The font name actually written into the .docx — a safe substitute for
 * fonts that would otherwise silently fall back to whatever Word picks. */
export function docxSafeFontName(fontId) {
    return DOCX_FONT_SUBSTITUTES[fontId] ?? docxFontName(fontId);
}
// ─── DOCX compatibility ─────────────────────────────────────────────────────
/** Returns true if this template's visual design can be reasonably replicated
 * in a Word/DOCX document — single-column, no CSS-only colour blocks or
 * sidebar panels that Word cannot represent. Show the DOCX export option only
 * when this returns true; always offer PDF. */
export function isDocxCompatible(t) {
    if (t.layout !== 'single')
        return false;
    const incompatibleHeaders = ['color-block', 'banner', 'vertical', 'terminal', 'typo-hero'];
    if (incompatibleHeaders.includes(t.header))
        return false;
    if (t.features.magazine)
        return false; // multi-column body can't be expressed in Word
    return true;
}
// ─── Paper & spacing ────────────────────────────────────────────────────────
export const PAPER_SIZES = {
    a4: { name: 'A4', w: 794, h: 1123, label: '210 × 297 mm' },
    letter: { name: 'US Letter', w: 816, h: 1056, label: '8.5 × 11 in' },
    legal: { name: 'US Legal', w: 816, h: 1344, label: '8.5 × 14 in' },
};
export const SPACING_PRESETS = {
    tight: {
        padY: 11,
        padX: 13,
        sectionGap: 4.5,
        entryGap: 3.0,
        bulletGap: 0.8,
        headingGap: 2.0,
        lineHeight: 1.36,
    },
    standard: {
        padY: 14,
        padX: 15,
        sectionGap: 6.5,
        entryGap: 4.2,
        bulletGap: 1.2,
        headingGap: 2.6,
        lineHeight: 1.5,
    },
    spacious: {
        padY: 18,
        padX: 19,
        sectionGap: 8.5,
        entryGap: 5.6,
        bulletGap: 1.5,
        headingGap: 3.2,
        lineHeight: 1.66,
    },
};
// ─── The catalogue ──────────────────────────────────────────────────────────
const ORD = {
    std: ['summary', 'experience', 'skills', 'education', 'projects', 'certs', 'languages'],
    eduFirst: ['education', 'experience', 'skills', 'projects', 'certs', 'languages', 'summary'],
    expFirst: ['summary', 'experience', 'education', 'skills', 'projects', 'certs', 'languages'],
    skillsFirst: ['summary', 'skills', 'experience', 'education', 'projects', 'certs', 'languages'],
    projFirst: ['summary', 'projects', 'experience', 'skills', 'education', 'certs', 'languages'],
    finance: ['education', 'experience', 'certs', 'skills', 'projects', 'languages', 'summary'],
};
export const T_DEFAULT = {
    layout: 'single',
    sidebarW: 30,
    sidebarTypes: ['skills', 'languages'],
    header: 'left',
    heading: 'rule',
    font: 'inter',
    accent: 'titles',
    density: 'standard',
    photo: false,
    order: ORD.std,
    features: {},
    ats: 'yes',
};
function t(cat, id, name, style, distinctive, overrides = {}) {
    return {
        ...T_DEFAULT,
        ...overrides,
        id: `${cat}-${id}`,
        cat,
        name,
        style,
        distinctive,
        features: { ...(overrides.features ?? {}) },
    };
}
/** Builds a template that keeps its historical `ats-*` id — real saved
 * resumes reference this string — while filing it under `cat`, its actual
 * design category. Used only for the five templates that survived the
 * standalone "ATS-Safe Classic" category being folded away (ATS-safety is
 * a per-template badge via the `ats` field, not a design language of its
 * own — see TEMPLATE_ALIASES below for the ids that didn't survive). */
function legacyAtsTemplate(cat, legacyId, name, style, distinctive, overrides = {}) {
    return {
        ...T_DEFAULT,
        ...overrides,
        id: `ats-${legacyId}`,
        cat,
        name,
        style,
        distinctive,
        features: { ...(overrides.features ?? {}) },
    };
}
export const TEMPLATES = [
    // ───── Modern Minimal ─────
    t('minimal', 'single-line', 'Single Accent Line', 'One thin line under header', 'The entire color usage is a single line under the header.', { font: 'inter', header: 'left', heading: 'plain', accent: 'line', density: 'spacious' }),
    t('minimal', 'icon-accent', 'Icon-Accented', 'Line-icons in contact row', 'Icons appear only in the contact row, nowhere else.', {
        font: 'figtree',
        heading: 'plain',
        accent: 'titles',
        ats: 'conditional',
        features: { contactIcons: true },
    }),
    t('minimal', 'underline-head', 'Underline Headers', 'Accent underlined titles', 'Color used purely as a navigation cue across section titles.', { font: 'inter', heading: 'underline', accent: 'titles' }),
    t('minimal', 'dot-divider', 'Dot-Divider', 'Quiet, near-monochrome', 'Contact items dot-separated; soft gray dividers between sections.', {
        font: 'figtree',
        heading: 'plain',
        accent: 'line',
        features: { dotContact: true, softDividers: true },
    }),
    t('minimal', 'dates-right', 'Right-Aligned Dates', 'Date column by weight', 'Clean rhythm from a consistent right-aligned date column.', {
        font: 'inter',
        heading: 'plain',
        accent: 'line',
        order: ORD.expFirst,
        features: { datesRight: true },
    }),
    t('minimal', 'two-tone', 'Two-Tone', 'Only the name is colored', 'Most restrained color use in the library — just the name.', { font: 'inter', heading: 'plain', accent: 'name' }),
    t('minimal', 'centered', 'Centered Header', 'Centered head, left body', 'Centered header against an otherwise left-aligned body.', { font: 'inter', header: 'center', heading: 'centered', accent: 'line' }),
    t('minimal', 'subtle-sidebar', 'Subtle Sidebar', 'Slim 18% tinted rail', 'Smallest possible two-zone split before a true two-column.', {
        font: 'inter',
        layout: 'sidebar-left',
        sidebarW: 18,
        sidebarTypes: ['skills', 'languages'],
        accent: 'sidebar',
        ats: 'conditional',
        heading: 'plain',
    }),
    // These three keep their historical `ats-*` id (never `cat`, since the id
    // is what's persisted on real saved resumes) — the standalone "ATS-Safe
    // Classic" category was folded away since ATS-safety is already a
    // per-template badge (see `ats` field / AtsBadge), not a design language
    // of its own. Filed here because they're visually the plainest, most
    // monochrome templates in the catalogue, which is what "minimal" means.
    legacyAtsTemplate('minimal', 'classic-serif', 'Classic Serif', 'Serif, rule under each heading', 'Thin horizontal rule under every section heading.', { font: 'newsreader', heading: 'rule', accent: 'none', order: ORD.std }),
    legacyAtsTemplate('minimal', 'chrono-reverse', 'Chronological Reverse', 'Right-aligned date column', 'Dates locked to a right-aligned column for fast scanning.', {
        font: 'inter',
        heading: 'caps',
        accent: 'none',
        order: ORD.expFirst,
        features: { datesRight: true },
    }),
    legacyAtsTemplate('minimal', 'two-line', 'Two-Line Header', 'Name + tagline, contact pipe-row', 'Role tagline directly under the name.', { font: 'inter', header: 'two-line', heading: 'caps', accent: 'none', order: ORD.eduFirst }),
    // ───── Two-Column Compact ─────
    t('twocol', 'left-classic', 'Left Sidebar Classic', '30/70 tinted sidebar', 'The baseline two-column template all others riff on.', {
        layout: 'sidebar-left',
        sidebarW: 30,
        accent: 'sidebar',
        ats: 'no',
        photo: true,
        sidebarTypes: ['skills', 'languages', 'certs'],
        heading: 'caps',
    }),
    t('twocol', 'right-sidebar', 'Right Sidebar', 'Mirror of the classic', 'Pure mirror — sidebar on the right.', {
        layout: 'sidebar-right',
        sidebarW: 30,
        accent: 'sidebar',
        ats: 'no',
        photo: true,
        sidebarTypes: ['skills', 'languages', 'certs'],
        heading: 'caps',
    }),
    t('twocol', 'narrow', 'Narrow Sidebar', '24% icon/tag rail', 'Sidebar is purely tag-based, not text lists.', {
        layout: 'sidebar-left',
        sidebarW: 24,
        accent: 'sidebar',
        ats: 'no',
        sidebarTypes: ['skills', 'languages'],
        heading: 'caps',
        features: { tagCloud: true },
    }),
    t('twocol', 'wide', 'Wide Sidebar', '40% education-weighted', 'Shifts more weight to the sidebar than any other variant.', {
        layout: 'sidebar-left',
        sidebarW: 40,
        accent: 'sidebar',
        ats: 'no',
        photo: true,
        sidebarTypes: ['summary', 'skills', 'education'],
        heading: 'caps',
    }),
    t('twocol', 'colored-block', 'Colored Sidebar Block', 'High-contrast solid rail', 'Highest-contrast visual split in the category.', {
        layout: 'sidebar-left',
        sidebarW: 32,
        accent: 'sidebar',
        ats: 'no',
        photo: true,
        sidebarTypes: ['skills', 'languages', 'certs'],
        heading: 'caps',
        features: { solidSidebar: true },
    }),
    t('twocol', 'banner-2col', 'Top Banner + Two Column', 'Banner over split body', 'Only variant where the header breaks out of the columns.', {
        layout: 'sidebar-left',
        sidebarW: 30,
        header: 'banner',
        accent: 'banner',
        ats: 'no',
        photo: true,
        sidebarTypes: ['skills', 'languages'],
        heading: 'caps',
    }),
    t('twocol', 'skill-bars', 'Skill-Bar Sidebar', 'Proficiency bars', 'Skills as horizontal proficiency bars.', {
        layout: 'sidebar-left',
        sidebarW: 30,
        accent: 'sidebar',
        ats: 'no',
        photo: true,
        sidebarTypes: ['skills', 'languages'],
        heading: 'caps',
        features: { skillBars: true },
    }),
    t('twocol', 'icon-grid', 'Icon Grid Sidebar', 'Tools as icon grid', 'Most visual / least text-dense sidebar treatment.', {
        layout: 'sidebar-left',
        sidebarW: 30,
        accent: 'sidebar',
        ats: 'no',
        photo: true,
        sidebarTypes: ['skills', 'certs', 'languages'],
        heading: 'caps',
        features: { tagCloud: true },
    }),
    t('twocol', 'timeline-main', 'Timeline Main Column', 'Graphical experience rail', 'Main column renders experience as a vertical timeline.', {
        layout: 'sidebar-left',
        sidebarW: 28,
        accent: 'sidebar',
        ats: 'no',
        photo: true,
        sidebarTypes: ['skills', 'languages'],
        heading: 'caps',
        features: { timeline: true },
    }),
    t('twocol', 'asymmetric', 'Asymmetric Compact', 'Badge bridges the seam', 'Most distinctive layout — initials badge bridging both columns.', {
        layout: 'sidebar-left',
        sidebarW: 35,
        accent: 'sidebar',
        ats: 'no',
        photo: true,
        sidebarTypes: ['skills', 'languages', 'certs'],
        heading: 'caps',
        features: { seamBadge: true },
    }),
    // ───── Technical / Developer ─────
    t('dev', 'projects-first', 'Projects-First Stack', 'Projects above experience', 'Projects ranked above Experience; tech-stack pill row per project.', {
        font: 'inter',
        heading: 'caps',
        accent: 'titles',
        order: ORD.projFirst,
        features: { monoTags: true },
    }),
    t('dev', 'github-stats', 'GitHub Stats Strip', 'Credibility badge row', 'Header doubles as a quick-credibility stats strip.', {
        font: 'inter',
        heading: 'caps',
        accent: 'titles',
        order: ORD.projFirst,
        ats: 'conditional',
        features: { statsStrip: true, monoTags: true },
    }),
    t('dev', 'monospace-accent', 'Monospace Accent', 'Mono stack labels', 'Monospace used surgically on stack labels & dividers.', {
        font: 'inter',
        heading: 'plain',
        accent: 'titles',
        order: ORD.projFirst,
        features: { monoTags: true, monoTitles: true },
    }),
    t('dev', 'terminal-header', 'Terminal-Card Header', 'Code-editor window head', 'Header styled like a restrained code-editor window.', {
        font: 'inter',
        header: 'terminal',
        heading: 'caps',
        accent: 'titles',
        order: ORD.projFirst,
        features: { monoTags: true },
    }),
    t('dev', 'tag-cloud', 'Tag-Cloud Skills', 'Sized pill tags', 'Skill emphasis conveyed via varied tag sizes, grouped by category.', {
        font: 'figtree',
        heading: 'caps',
        accent: 'titles',
        order: ORD.skillsFirst,
        ats: 'conditional',
        features: { tagCloud: true },
    }),
    t('dev', 'split-stack', 'Split Tech-Stack', '3-column skills grid', 'Skills shown as an internal grid.', {
        font: 'inter',
        heading: 'caps',
        accent: 'titles',
        order: ORD.projFirst,
        features: { skillGrid: true, monoTags: true },
    }),
    t('dev', 'project-cards', 'Project-Card Grid', 'Bordered project cards', 'Projects render as a 2-column grid of bordered cards.', {
        font: 'inter',
        heading: 'caps',
        accent: 'titles',
        order: ORD.projFirst,
        ats: 'conditional',
        features: { projectCards: true, monoTags: true },
    }),
    t('dev', 'inline-badges', 'Inline Badge Skills', 'Shields-style badges', 'Skills as small colored shield-style badges in a flowing row.', {
        font: 'inter',
        heading: 'caps',
        accent: 'titles',
        order: ORD.skillsFirst,
        ats: 'conditional',
        features: { inlineBadges: true },
    }),
    t('dev', 'minimal-dev', 'Minimal Dev Classic', 'Metric-led bullets', 'No flourishes — differentiation is metric-led bullet guidance.', { font: 'inter', heading: 'rule', accent: 'none', order: ORD.projFirst }),
    t('dev', 'open-source', 'Open-Source Highlight', 'OSS & hackathon section', 'Dedicated Open Source & Hackathons section above Education.', {
        font: 'inter',
        heading: 'caps',
        accent: 'titles',
        order: ['summary', 'projects', 'experience', 'skills', 'education', 'certs'],
        features: { monoTags: true, ossNote: true },
    }),
    // ───── Creative / Portfolio ─────
    t('creative', 'asym-split', 'Asymmetric Split', 'Angled divider', 'An angled divider is the signature visual element.', {
        font: 'figtree',
        header: 'left',
        heading: 'plain',
        accent: 'titles',
        ats: 'no',
        features: { angledSplit: true },
    }),
    t('creative', 'bold-block', 'Bold Color Block', 'Full-width color band', 'Maximum color confidence in one zone.', { font: 'figtree', header: 'color-block', heading: 'caps', accent: 'block', ats: 'no' }),
    t('creative', 'photo-circle', 'Photo Circle Header', 'Photo as anchor', 'Built around a circular cropped photo beside display-type name.', {
        font: 'figtree',
        header: 'left',
        heading: 'plain',
        accent: 'titles',
        ats: 'no',
        photo: true,
        features: { bigPhoto: true },
    }),
    t('creative', 'portfolio-strip', 'Portfolio Strip', 'Work-sample strip', 'A thin work-sample strip sits above standard sections.', {
        font: 'figtree',
        heading: 'plain',
        accent: 'titles',
        ats: 'no',
        features: { portfolioStrip: true },
    }),
    t('creative', 'typo-hero', 'Typographic Hero', 'Oversized display name', 'Typography itself is the design statement.', { font: 'franklin', header: 'typo-hero', heading: 'plain', accent: 'name', ats: 'no' }),
    t('creative', 'pastel-sidebar', 'Pastel Sidebar', 'Soft pastel rail', 'Softest, least corporate palette in the library.', {
        font: 'figtree',
        layout: 'sidebar-left',
        sidebarW: 32,
        accent: 'sidebar',
        ats: 'no',
        photo: true,
        sidebarTypes: ['skills', 'languages', 'certs'],
        heading: 'plain',
        features: { pastel: true },
    }),
    t('creative', 'grid-mosaic', 'Grid Mosaic Skills', 'Colorful skill tiles', 'Skills treated as a visual mosaic, not a list.', {
        font: 'figtree',
        heading: 'caps',
        accent: 'titles',
        ats: 'no',
        order: ORD.skillsFirst,
        features: { mosaic: true },
    }),
    t('creative', 'magazine', 'Magazine-Style', 'Multi-column body, pull-quote', 'Editorial feel; summary styled as a pull-quote.', {
        font: 'newsreader',
        heading: 'plain',
        accent: 'titles',
        ats: 'no',
        features: { magazine: true, pullQuote: true },
    }),
    t('creative', 'line-art', 'Line-Art Accents', 'Mono base + one accent', 'Most restrained creative — a "creative but safe" bridge.', {
        font: 'figtree',
        heading: 'underline',
        accent: 'titles',
        ats: 'conditional',
        features: { contactIcons: true },
    }),
    t('creative', 'vertical-band', 'Vertical Name Band', 'Rotated name margin', 'Name rotated vertically along the left margin.', {
        font: 'franklin',
        header: 'vertical',
        heading: 'plain',
        accent: 'band',
        ats: 'no',
        features: { verticalBand: true },
    }),
    // ───── Executive / Senior ─────
    t('exec', 'serif-headline', 'Serif Headline', 'Serif name, sans body', 'Boardroom-formal pairing — the category reference.', {
        font: 'newsreader',
        heading: 'rule',
        accent: 'titles',
        density: 'spacious',
        order: ORD.expFirst,
    }),
    t('exec', 'letterhead', 'Letterhead Style', 'Top + bottom rules frame head', 'Corporate-letterhead framing around the name block.', { font: 'georgia', header: 'letterhead', heading: 'rule', accent: 'none', order: ORD.expFirst }),
    t('exec', 'metric-lead', 'Achievement-Metric Lead', 'Standout metric row', 'Leads with 3–4 quantified achievement metrics instead of a summary.', {
        font: 'inter',
        heading: 'caps',
        accent: 'titles',
        ats: 'conditional',
        order: ORD.expFirst,
        features: { metricRow: true },
    }),
    t('exec', 'two-tone', 'Two-Tone Executive', 'Dark header band', 'Only executive variant with a dark header zone.', {
        font: 'inter',
        header: 'color-block',
        heading: 'caps',
        accent: 'block',
        ats: 'conditional',
        order: ORD.expFirst,
        features: { darkBlock: true },
    }),
    t('exec', 'career-timeline', 'Career Timeline Header', 'Span graphic under name', 'A small career timeline compresses 15–20 years into one line.', {
        font: 'inter',
        heading: 'rule',
        accent: 'titles',
        ats: 'conditional',
        order: ORD.expFirst,
        features: { careerTimeline: true },
    }),
    t('exec', 'centered-formal', 'Centered Formal', 'Symmetrical, centered', 'Most formal / traditional alignment in the library.', { font: 'georgia', header: 'center', heading: 'centered', accent: 'none', order: ORD.expFirst }),
    t('exec', 'board-minimal', 'Board-Ready Minimal', 'Sparse, pure type', 'The most restrained template across all categories.', {
        font: 'newsreader',
        heading: 'plain',
        accent: 'none',
        density: 'spacious',
        order: ORD.expFirst,
    }),
    t('exec', 'two-column', 'Executive Two-Column', 'Right credentials rail', 'Narrow right column for memberships / certifications.', {
        font: 'inter',
        layout: 'sidebar-right',
        sidebarW: 28,
        accent: 'sidebar',
        ats: 'no',
        sidebarTypes: ['certs', 'skills', 'languages'],
        heading: 'caps',
        order: ORD.expFirst,
    }),
    t('exec', 'quote-summary', 'Quote-Style Summary', 'Pull-quote summary', 'Summary as a larger italic serif statement piece.', {
        font: 'newsreader',
        heading: 'rule',
        accent: 'titles',
        order: ORD.std,
        features: { pullQuote: true },
    }),
    t('exec', 'legacy-brand', 'Legacy Brand', 'Monogram beside name', 'Personal monogram mark — a personal-branding feel.', {
        font: 'georgia',
        header: 'monogram',
        heading: 'rule',
        accent: 'titles',
        order: ORD.expFirst,
        features: { monogram: true },
    }),
    // These two keep their historical `ats-*` id — see the note above the
    // relocated minimal entries. Filed here because "formal/declaration/
    // finance" reads as executive, not minimal.
    legacyAtsTemplate('exec', 'gov-compact', 'Compact Government', 'Tight serif, formal norm', 'Closes with a Declaration line, common in formal CV formats.', {
        font: 'georgia',
        heading: 'rule',
        accent: 'none',
        density: 'tight',
        order: ORD.eduFirst,
        features: { declaration: true },
    }),
    legacyAtsTemplate('exec', 'banker', 'Banker / Finance', 'Conservative serif, grade prominent', 'Grade / percentage bolded inline beside each degree.', {
        font: 'georgia',
        heading: 'rule',
        accent: 'none',
        order: ORD.finance,
        features: { declaration: true, gradeBold: true },
    }),
    // ───── Academic / Research ─────
    t('academic', 'pubs-first', 'Publications-First', 'Research elevated', 'Projects/publications elevated above almost everything else.', {
        font: 'newsreader',
        heading: 'rule',
        accent: 'titles',
        order: ['summary', 'projects', 'education', 'experience', 'skills', 'certs'],
        features: { multipage: true },
    }),
    t('academic', 'funding', 'Grant / Funding Highlight', 'Funding table', 'Dedicated funding section with Amount | Role | Year.', {
        font: 'newsreader',
        heading: 'rule',
        accent: 'titles',
        order: ORD.eduFirst,
        features: { multipage: true, fundingNote: true },
    }),
    t('academic', 'running-header', 'Multi-Page Header', 'Repeating running head', 'Name + page marker repeats on every page.', {
        font: 'georgia',
        heading: 'rule',
        accent: 'titles',
        order: ORD.eduFirst,
        features: { multipage: true, runningHeader: true },
    }),
    t('academic', 'statement-lead', 'Research Statement Lead', 'Narrative opening', 'Opens with a longer research statement before any lists.', {
        font: 'newsreader',
        heading: 'rule',
        accent: 'titles',
        order: ORD.std,
        features: { multipage: true, pullQuote: true },
    }),
    t('academic', 'two-col-compact', 'Two-Column Academic', 'Condensed single page', 'The only two-column, single-page-condensed academic variant.', {
        font: 'newsreader',
        layout: 'sidebar-left',
        sidebarW: 30,
        accent: 'sidebar',
        ats: 'no',
        sidebarTypes: ['skills', 'languages', 'certs'],
        heading: 'rule',
        order: ORD.eduFirst,
    }),
];
export const DEFAULT_TEMPLATE_ID = 'minimal-single-line';
const TEMPLATE_BY_ID = Object.fromEntries(TEMPLATES.map((tpl) => [tpl.id, tpl]));
/** Ids retired from the catalogue (either exact duplicates of a sibling, or
 * folded away when the standalone "ATS-Safe Classic" category was removed)
 * mapped to the closest surviving template. A resume saved with one of these
 * ids before the catalogue changed must keep rendering — falling through to
 * the global default would be a real, surprising appearance change for
 * whoever already has it selected — so these resolve through here rather
 * than disappearing from TEMPLATE_BY_ID. Never resolvable from the picker;
 * only reachable via `getTemplate()` for old saved data. */
const TEMPLATE_ALIASES = {
    'ats-combination': 'ats-classic-serif',
    'ats-modern-sans': 'ats-classic-serif',
    'ats-functional': 'ats-classic-serif',
    'ats-numbered': 'ats-classic-serif',
    'ats-wide-margin': 'exec-board-minimal',
    'minimal-spacious': 'minimal-single-line',
    'minimal-color-block': 'creative-bold-block',
    'academic-chrono': 'academic-pubs-first',
    'academic-teaching': 'academic-pubs-first',
    'academic-conf-table': 'academic-pubs-first',
    'academic-skills-tools': 'academic-pubs-first',
    'academic-numbered-pubs': 'academic-pubs-first',
};
export function getTemplate(id) {
    const resolved = id && TEMPLATE_ALIASES[id] ? TEMPLATE_ALIASES[id] : id;
    return ((resolved && TEMPLATE_BY_ID[resolved]) || TEMPLATE_BY_ID[DEFAULT_TEMPLATE_ID] || TEMPLATES[0]);
}
// ─── Defensive normalization for export payloads ────────────────────────────
// The backend content schema is intentionally loose (design/basics optional,
// spacing passthrough) so old/partial payloads still validate. Exports must
// therefore fill defaults instead of trusting every field to exist.
export function resolveDesign(content, t) {
    const d = (content?.design ?? {});
    const preset = SPACING_PRESETS[t.density] ?? SPACING_PRESETS.standard;
    return {
        ...d,
        template: d.template ?? t.id,
        accent: d.accent ?? 'teal',
        font: d.font ?? t.font,
        paperSize: d.paperSize ?? 'a4',
        scale: typeof d.scale === 'number' && d.scale > 0 ? d.scale : 100,
        spacing: { ...preset, ...(d.spacing ?? {}) },
    };
}
export function resolveBasics(content) {
    const b = (content?.basics ?? {});
    return {
        name: b.name ?? '',
        headline: b.headline ?? '',
        photo: b.photo ?? '',
        showPhoto: b.showPhoto ?? false,
        email: b.email ?? '',
        phone: b.phone ?? '',
        location: b.location ?? '',
        links: Array.isArray(b.links) ? b.links : [],
    };
}
// ─── Pure content helpers shared by preview + exports ───────────────────────
export function initialsOf(name) {
    return ((name || '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0] || '')
        .join('')
        .toUpperCase() || '?');
}
/** Strip HTML tags for plain-text analysis (word counts, digit detection,
 * AI prompts) — rich-text fields hold sanitized HTML, not plain strings. */
export function stripHtml(html) {
    if (!html)
        return '';
    return html
        .replace(/<(p|li|br|div)[^>]*>/gi, ' ')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/** Legacy discrete `bullets[]` → the same `<ul><li>` shape `description` now
 * stores, so old and new experience entries render through one code path. */
export function bulletsToHtml(bullets) {
    const items = bullets.filter((b) => stripHtml(b));
    if (!items.length)
        return '';
    return `<ul>${items.map((b) => `<li>${b}</li>`).join('')}</ul>`;
}
/** Resolve an experience item's highlights as rich HTML, preferring the new
 * `description` field and falling back to legacy `bullets[]`. */
export function expHtml(it) {
    return it.description && it.description.trim() ? it.description : bulletsToHtml(it.bullets);
}
/** Split a highlights HTML blob back into plain-text lines — one per `<li>`,
 * or the whole stripped text as a single line if it holds no list markup. */
export function htmlLines(html) {
    const items = html.match(/<li[^>]*>([\s\S]*?)<\/li>/gi);
    if (items)
        return items.map((m) => stripHtml(m)).filter(Boolean);
    const plain = stripHtml(html);
    return plain ? [plain] : [];
}
/** Best-effort 4-digit year out of a free-text date string like "Mar 2023". */
function extractYear(text) {
    if (!text)
        return null;
    const m = text.match(/\b(19|20)\d{2}\b/);
    return m ? Number(m[0]) : null;
}
/** Real, content-derived numbers for header treatments (statsStrip,
 * careerTimeline, metricRow) — never fabricated placeholder data. */
export function deriveResumeStats(content) {
    const exp = content.sections.find((s) => s.type === 'experience');
    const projects = content.sections.find((s) => s.type === 'projects');
    const skills = content.sections.find((s) => s.type === 'skills');
    const certs = content.sections.find((s) => s.type === 'certs');
    const years = new Set();
    let earliest = null;
    const currentYear = new Date().getFullYear();
    let latest = null;
    for (const item of exp?.items ?? []) {
        const startYear = extractYear(item.start);
        if (startYear) {
            years.add(startYear);
            if (earliest === null || startYear < earliest)
                earliest = startYear;
        }
        const endYear = item.current ? currentYear : extractYear(item.end);
        if (endYear) {
            years.add(endYear);
            if (latest === null || endYear > latest)
                latest = endYear;
        }
    }
    return {
        yearsExperience: earliest !== null ? Math.max(1, (latest ?? currentYear) - earliest) : null,
        roleCount: exp?.items.length ?? 0,
        projectCount: projects?.items.length ?? 0,
        skillCount: skills ? skills.groups.reduce((n, g) => n + g.items.length, 0) : 0,
        certCount: certs?.items.length ?? 0,
        timelineYears: Array.from(years).sort((a, b) => a - b),
    };
}
/** Content-driven readiness score (0-100) — no AI, purely derived from
 * presence/quality signals in the resume content itself plus the chosen
 * template's ATS rating.
 *
 * This is the single source of truth for the score: it's imported verbatim
 * by the frontend Review tab (live, on every edit) and by the backend
 * (persisted to `Resume.readinessScore` on every save) so the two never
 * disagree — they used to be two hand-maintained copies that drifted. */
export function computeReadiness(content) {
    let score = 40;
    const checks = [];
    // Template ATS rating — the Review tab claims to run "ATS & quality checks",
    // so it must consider the template itself, not just the content. A perfect
    // resume on a two-column visual template still fails strict parsers.
    const template = getTemplate(content.design.template);
    const atsCopy = ATS_RATING_COPY[template.ats];
    if (template.ats === 'yes')
        score += 5;
    checks.push({
        id: 'template-ats',
        label: 'Template ATS compatibility',
        detail: `${template.name}: ${atsCopy.detail}`,
        pass: template.ats === 'yes',
        warn: template.ats === 'conditional',
    });
    const summary = content.sections.find((s) => s.type === 'summary');
    const hasSummary = !!(summary && summary.visible && stripHtml(summary.text).length > 120);
    if (hasSummary)
        score += 12;
    checks.push({
        id: 'summary',
        label: 'Professional summary',
        detail: hasSummary ? 'Strong length & specificity' : 'Add 2–3 sentences',
        pass: hasSummary,
    });
    const exp = content.sections.find((s) => s.type === 'experience');
    const bulletCount = exp ? exp.items.reduce((n, i) => n + htmlLines(expHtml(i)).length, 0) : 0;
    const quantified = exp
        ? exp.items.reduce((n, i) => n + htmlLines(expHtml(i)).filter((line) => /\d/.test(line)).length, 0)
        : 0;
    if (exp && exp.visible) {
        score += Math.min(18, bulletCount * 1.2);
        score += Math.min(12, quantified * 2);
    }
    const quantRatio = bulletCount > 0 ? quantified / bulletCount : 0;
    checks.push({
        id: 'quantified',
        label: 'Quantified achievements',
        detail: `${quantified} of ${bulletCount} bullets include metrics`,
        pass: quantRatio >= 0.4,
        warn: quantified > 0 && quantRatio < 0.4,
    });
    const skills = content.sections.find((s) => s.type === 'skills');
    if (skills && skills.visible) {
        score += Math.min(10, skills.groups.reduce((n, g) => n + g.items.length, 0) * 0.6);
    }
    const education = content.sections.find((s) => s.type === 'education');
    if (education?.visible)
        score += 4;
    const projects = content.sections.find((s) => s.type === 'projects');
    if (projects?.visible)
        score += 4;
    checks.push({
        id: 'contact',
        label: 'Contact details complete',
        detail: 'Email & phone present',
        pass: !!(content.basics.email && content.basics.phone),
    });
    checks.push({
        id: 'skills-coverage',
        label: 'Skills coverage',
        detail: 'Grouped & relevant to target roles',
        pass: !!skills && skills.groups.some((g) => g.items.length > 0),
    });
    // Visible-text length, not the serialized JSON blob — markup/control
    // characters would otherwise inflate this and misrepresent what "too
    // long" means to an actual reader.
    const visibleTextLength = stripHtml(summary?.text ?? '').length +
        (exp?.items ?? []).reduce((n, i) => n + stripHtml(expHtml(i)).length, 0);
    checks.push({
        id: 'length',
        label: 'Length',
        detail: 'Aim for 1–2 pages',
        pass: visibleTextLength <= 4000,
        warn: visibleTextLength > 4000,
    });
    return { score: Math.max(0, Math.min(100, Math.round(score))), checks };
}
const JOB_MATCH_STOPWORDS = new Set([
    'a',
    'an',
    'the',
    'and',
    'or',
    'but',
    'if',
    'then',
    'else',
    'for',
    'to',
    'of',
    'in',
    'on',
    'at',
    'by',
    'with',
    'from',
    'as',
    'is',
    'are',
    'was',
    'were',
    'be',
    'been',
    'being',
    'this',
    'that',
    'these',
    'those',
    'it',
    'its',
    'you',
    'your',
    'we',
    'our',
    'they',
    'their',
    'he',
    'she',
    'his',
    'her',
    'will',
    'would',
    'can',
    'could',
    'should',
    'may',
    'might',
    'must',
    'shall',
    'not',
    'no',
    'yes',
    'do',
    'does',
    'did',
    'have',
    'has',
    'had',
    'having',
    // job-posting boilerplate — common enough to be noise, not a real signal
    'experience',
    'experienced',
    'years',
    'year',
    'role',
    'roles',
    'job',
    'jobs',
    'work',
    'working',
    'works',
    'team',
    'teams',
    'company',
    'companies',
    'opportunity',
    'opportunities',
    'candidate',
    'candidates',
    'position',
    'positions',
    'required',
    'requirements',
    'requirement',
    'preferred',
    'plus',
    'strong',
    'excellent',
    'good',
    'ability',
    'abilities',
    'skills',
    'skill',
    'including',
    'include',
    'includes',
    'etc',
    'responsibilities',
    'responsible',
    'duties',
    'looking',
    'join',
    'apply',
    'applicants',
    'across',
    'within',
    'other',
    'various',
    'new',
    'more',
    'most',
    'all',
    'any',
    'some',
    'such',
    'also',
    'about',
    'into',
    'through',
    'per',
    'well',
    'using',
    'use',
    'used',
    'who',
    'what',
    'when',
    'where',
    'why',
    'how',
    'which',
    'employer',
    'employment',
    'benefits',
    'salary',
    'full',
    'time',
    'part',
]);
/** Lowercased, deduplicated significant words from free text — keeps
 * tech-ish punctuation (C++, C#, .NET, Node.js) that a stricter
 * alphanumeric-only filter would otherwise mangle. */
function extractKeywords(text) {
    const words = text
        .toLowerCase()
        .replace(/[^a-z0-9+.#\s-]/g, ' ')
        .split(/\s+/)
        .map((w) => w.replace(/^[-.]+|[-.]+$/g, '').trim())
        .filter((w) => w.length >= 3 && !/^\d+$/.test(w) && !JOB_MATCH_STOPWORDS.has(w));
    return Array.from(new Set(words));
}
/** Compares a job description's significant keywords against everything
 * searchable in the resume (headline, summary, skills, experience bullets,
 * education) and reports what's covered and what's missing. */
export function analyzeJobMatch(content, jobDescription) {
    const jdKeywords = extractKeywords(jobDescription);
    if (!jdKeywords.length)
        return { score: 0, matched: [], missing: [] };
    const skills = content.sections.find((s) => s.type === 'skills');
    const summary = content.sections.find((s) => s.type === 'summary');
    const exp = content.sections.find((s) => s.type === 'experience');
    const education = content.sections.find((s) => s.type === 'education');
    const resumeParts = [
        content.basics.headline,
        stripHtml(summary?.text ?? ''),
        ...(skills?.groups.flatMap((g) => g.items) ?? []),
        ...(exp?.items.flatMap((i) => [i.role, i.company, stripHtml(expHtml(i))]) ?? []),
        ...(education?.items.flatMap((e) => [e.degree, e.school]) ?? []),
    ];
    const resumeText = resumeParts.join(' ').toLowerCase();
    const matched = [];
    const missing = [];
    for (const kw of jdKeywords) {
        (resumeText.includes(kw) ? matched : missing).push(kw);
    }
    return { score: Math.round((matched.length / jdKeywords.length) * 100), matched, missing };
}
